<template>
  <PanelPage ref="useAlerts" :loading-overlay="loadingOverlay" md="12">
    <v-card>
      <v-card-title>Modify Ipsum</v-card-title>
      <v-card-title>
        <v-autocomplete
          label="Ipsum Device to modify"
          :items="ipsumdevices"
          v-model="ipsumToModifyId"
          item-title="visual_name"
          item-value="id"
        />
      </v-card-title>
      <template v-if="ipsumToModifyId">
        <v-divider thickness="4"/>
        <template v-if="lastPowers">
          <v-card-title>Last Ipsum Data</v-card-title>
          <v-card-text>
            <v-table density="compact">
              <thead>
              <tr>
                <th>Time</th>
                <!--suppress JSUnusedLocalSymbols -->
                <th v-for="(value, index) in lastPowers['powers']">P{{ index + 1 }}</th>
              </tr>
              </thead>
              <tbody>
              <tr>
                <td>{{ lastPowerTime }} (Paris Time)</td>
                <!--suppress JSUnusedLocalSymbols -->
                <td v-for="(value, index) in lastPowers['powers']">{{ value }} W</td>
              </tr>
              </tbody>
            </v-table>
          </v-card-text>
          <v-divider thickness="4"/>
        </template>
        <v-card-title>Ipsum settings</v-card-title>
        <v-form @submit.prevent="submit" @reset="reset">
          <v-card-text>
            <v-row>
              <v-col>
                <v-text-field
                  v-model="form.alias.value.value"
                  :error-messages="form.alias.errorMessage.value"
                  label="Name/Alias of the Ipsum"
                />
              </v-col>
              <v-col>
                <v-text-field
                  v-model="form.token.value.value"
                  :error-messages="form.token.errorMessage.value"
                  label="TOKEN"
                />
              </v-col>
              <v-col>
                <v-text-field
                  v-model="form.expected_clamps.value.value"
                  :error-messages="form.expected_clamps.errorMessage.value"
                  label="Expected clamps"
                />
              </v-col>
            </v-row>
            <duration-picker
              v-model="form.autodrop_max_interval.value.value"
              show-enable
            >Delete data older than X time
            </duration-picker>
            <duration-picker
              v-model="form.autosave_min_interval.value.value"
              show-enable
            >Save data only every X time
            </duration-picker>
            <v-textarea
              v-model="form.note.value.value"
              :error-messages="form.note.errorMessage.value"
              label="Note"
            />
          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn type="reset">Reset</v-btn>
            <v-btn type="submit">Submit</v-btn>
          </v-card-actions>
        </v-form>
      </template>
    </v-card>
  </PanelPage>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { getFormFromSchema } from "../utils.js";
import { fetchIpsumDevices, modifyPutIpsum, fetchIpsumLastPower } from "../remote/ipsum.js";
import PanelPage from "../components/PanelPage.vue";
import DurationPicker from "../components/DurationPicker.vue";

// State
const useAlerts = ref(null);
const ipsumdevices = ref([]);
const ipsumToModifyId = ref(null); // From the main selector
const loadingOverlay = ref(false);
const lastPowers = ref(null);

// Form
// noinspection JSCheckFunctionSignatures
const schema = yup.object({
  alias: yup.string().required(),
  token: yup.string().required(),
  expected_clamps: yup.number().integer().moreThan(0).required(),
  autodrop_max_interval: yup.string().nullable(),
  autosave_min_interval: yup.string().nullable(),
  note: yup.string().notRequired(),
});

// Frontend Computed
const lastPowerTime = computed(() => {
  if (lastPowers.value === null) return null;
  const data = new Date(lastPowers.value['timestamp']);
  return data.toLocaleString();
});

const { handleSubmit, handleReset } = useForm({
  validationSchema: schema
});

const form = getFormFromSchema(schema);

function setIpsumDevice(ipsum_id) {
  const ipsum = ipsumdevices.value.find(el => el['id'] === ipsum_id);
  if (ipsum === undefined) {
    useAlerts.value.errorAlert("No IPSUM with that ID in the system", "Refresh the page or contact an administrator");
    return;
  }
  // Fields = Values
  form.alias.value.value = ipsum['alias'];
  form.token.value.value = ipsum['token'];
  form.expected_clamps.value.value = ipsum['expected_clamps'];
  form.autodrop_max_interval.value.value = ipsum['autodrop_max_interval'] || null;
  form.autosave_min_interval.value.value = ipsum['autosave_min_interval'] || null;
  form.note.value.value = ipsum['note'] || "";
}

watch(ipsumToModifyId, async (newValue) => {
  if (newValue !== null) {
    loadingOverlay.value = true;
    setIpsumDevice(newValue);
    try {
      lastPowers.value = await fetchIpsumLastPower(newValue);
    } catch (e) {
      useAlerts.value.warningAlert("Error during retrieve of Ipsum Last Power", "It's normal if the Ipsum never sent data");
      lastPowers.value = null;
    }
    loadingOverlay.value = false;
  }
});

async function reset() {
  // noinspection JSValidateTypes
  handleReset();
  setIpsumDevice(ipsumToModifyId.value);
}

// noinspection JSValidateTypes
const submit = handleSubmit(async (values) => {
  let payload = {
    alias: values['alias'],
    token: values['token'],
    expected_clamps: values['expected_clamps'],
    autodrop_max_interval: values['autodrop_max_interval'],
    autosave_min_interval: values['autosave_min_interval'],
    note: values['note'] || "",
  };
  console.log(payload);
  loadingOverlay.value = true;
  // Modify MoneyDestination via PUT
  try {
    await modifyPutIpsum(useAlerts, payload, ipsumToModifyId.value);
  } finally {
    ipsumdevices.value = await fetchIpsumDevices();
    loadingOverlay.value = false;
  }
});

let lastpowerPoll;

onMounted(async () => {
  loadingOverlay.value = true;
  // Retrieve IPSUMDevices
  try {
    ipsumdevices.value = await fetchIpsumDevices();
  } catch (e) {
    useAlerts.value.errorAlert('Impossible to load IPSUM Devices', e.toString());
  } finally {
    loadingOverlay.value = false;
  }
  // Set Last Power poller
  lastpowerPoll = setInterval(async () => {
    if (ipsumToModifyId.value !== null) {
      try {
        lastPowers.value = await fetchIpsumLastPower(ipsumToModifyId.value);
      } catch (e) {
        console.log(`Ipsum ${ipsumToModifyId.value} failed to retrieve LastPower`);
        lastPowers.value = null;
      }
    }
  }, 5000);
});

onUnmounted(() => {
  clearInterval(lastpowerPoll);
});

</script>

<style scoped>

</style>
